import { Component, OnDestroy, OnInit } from "@angular/core";
import { Sort } from "@angular/material/sort";
import { Store } from "@ngrx/store";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { EChartsOption } from "echarts";
import { fdsToken } from "../../charts/chart-tokens";
import {
  ThemeService,
  ThemeTypes,
  ThemeModelType,
} from "../../theme/theme.service";
import {
  ConfirmationDetails,
  ConfirmationService,
} from "../../confirmation-dialog/confirmation.service";
import { EaredDoveService } from "../../eared-dove/eared-dove.service";
import { PadlockService } from "../../padlock/padlock.service";
import { DeleteWorkspace } from "../../workspace/state/workspaces.actions";
import {
  Workspace,
  WorkspaceDetails,
} from "../../workspace/state/workspaces.model";

// Dashboard color palette
const CHART_COLORS = [
  "#1b4e6b",
  "#5C63A2",
  "#C068A8",
  "#EC7176",
  "#f4ab33",
  "#00876C",
  "#3498db",
  "#9b59b6",
  "#e74c3c",
  "#2ecc71",
];

// Canvas charts can't use CSS var(), so FDS tokens are read when options are built.
function chartColors() {
  return {
    CHART_TEXT_COLOR_LIGHT: fdsToken("--fds-color-grey-600"),
    CHART_TEXT_COLOR_DARK: fdsToken("--fds-color-grey-400"),
    CHART_RICH_NAME_COLOR_LIGHT: fdsToken("--fds-color-grey-600"),
    CHART_RICH_NAME_COLOR_DARK: fdsToken("--fds-color-grey-300"),
    CHART_RICH_VALUE_COLOR_LIGHT: fdsToken("--fds-color-grey-800"),
    CHART_RICH_VALUE_COLOR_DARK: fdsToken("--fds-color-grey-100"),
    CHART_TOOLTIP_BG_DARK: fdsToken("--fds-color-grey-700"),
    CHART_TOOLTIP_TEXT_DARK: fdsToken("--fds-color-grey-200"),
  };
}

@Component({
  selector: "app-user-platform-workspaces",
  templateUrl: "./user-platform-workspaces.component.html",
  styleUrls: ["./user-platform-workspaces.component.scss"],
  standalone: false,
})
export class UserPlatformWorkspacesComponent implements OnInit, OnDestroy {
  workspaces: WorkspaceDetails[] = [];
  loading = false;
  loadError: any = null;
  userID: string | null = null;
  isPlatformAdmin: boolean = false;

  // Dashboard chart options
  ownerChartOptions: EChartsOption = {};
  postgresChartOptions: EChartsOption = {};
  elasticsearchChartOptions: EChartsOption = {};

  // Summary stats
  totalWorkspaces = 0;
  totalPostgresSize = 0;
  totalElasticsearchSize = 0;

  displayedColumns: string[] = [
    // label removed per design — keep model data but don't display
    "name",
    "owner_email",
    "datasource_count",
    "model_count",
    "member_count",
    "postgres_size_bytes",
    "elasticsearch_size_bytes",
    "most_recent_api_request",
    "actions",
  ];

  destroyed$ = new Subject<boolean>();
  darkMode = false;

  constructor(
    private store: Store<any>,
    private earedDoveService: EaredDoveService,
    private padlockService: PadlockService,
    private confirmationService: ConfirmationService,
    private themeService: ThemeService,
  ) {}

  ngOnInit(): void {
    const decoded = this.padlockService.getDecodedToken();
    this.userID = decoded ? decoded.sub : null;
    this.isPlatformAdmin = decoded?.platform_admin === true;
    this.loadWorkspaces();

    this.themeService
      .watchTheme("selected-theme")
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value: unknown) => {
        this.darkMode =
          (value as ThemeModelType)?.theme === ThemeTypes.DarkTheme;
        this.updateDashboardCharts();
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadWorkspaces(): void {
    this.loading = true;
    this.loadError = null;

    this.earedDoveService
      .getWorkspaceDetails()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (workspaces: WorkspaceDetails[]) => {
          this.workspaces = workspaces;
          this.loading = false;
          this.updateDashboardCharts();
        },
        error: (error: any) => {
          this.loadError = error;
          this.loading = false;
        },
      });
  }

  private updateDashboardCharts(): void {
    this.calculateSummaryStats();
    this.buildOwnerChart();
    this.buildPostgresChart();
    this.buildElasticsearchChart();
  }

  private calculateSummaryStats(): void {
    this.totalWorkspaces = this.workspaces.length;
    this.totalPostgresSize = this.workspaces.reduce(
      (sum, ws) => sum + (ws.postgres_size_bytes || 0),
      0,
    );
    this.totalElasticsearchSize = this.workspaces.reduce(
      (sum, ws) => sum + (ws.elasticsearch_size_bytes || 0),
      0,
    );
  }

  private buildOwnerChart(): void {
    // Group workspaces by owner
    const ownerCounts = new Map<string, number>();
    this.workspaces.forEach((ws) => {
      const owner = ws.owner_email || "Unknown";
      ownerCounts.set(owner, (ownerCounts.get(owner) || 0) + 1);
    });

    const data = Array.from(ownerCounts.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    this.ownerChartOptions = this.createDonutChartOptions(
      data,
      "Workspaces by Owner",
    );
  }

  private buildPostgresChart(): void {
    const data = this.workspaces
      .filter((ws) => ws.postgres_size_bytes > 0)
      .map((ws) => ({
        name: ws.name,
        value: ws.postgres_size_bytes,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10); // Top 10

    this.postgresChartOptions = this.createDonutChartOptions(
      data,
      "PostgreSQL Storage",
      true,
    );
  }

  private buildElasticsearchChart(): void {
    const data = this.workspaces
      .filter((ws) => ws.elasticsearch_size_bytes > 0)
      .map((ws) => ({
        name: ws.name,
        value: ws.elasticsearch_size_bytes,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10); // Top 10

    this.elasticsearchChartOptions = this.createDonutChartOptions(
      data,
      "Elasticsearch Storage",
      true,
    );
  }

  private createDonutChartOptions(
    data: { name: string; value: number }[],
    title: string,
    formatAsBytes = false,
  ): EChartsOption {
    const self = this;
    const {
      CHART_TEXT_COLOR_LIGHT,
      CHART_TEXT_COLOR_DARK,
      CHART_RICH_NAME_COLOR_LIGHT,
      CHART_RICH_NAME_COLOR_DARK,
      CHART_RICH_VALUE_COLOR_LIGHT,
      CHART_RICH_VALUE_COLOR_DARK,
      CHART_TOOLTIP_BG_DARK,
      CHART_TOOLTIP_TEXT_DARK,
    } = chartColors();
    const textColor = this.darkMode
      ? CHART_TEXT_COLOR_DARK
      : CHART_TEXT_COLOR_LIGHT;
    const richNameColor = this.darkMode
      ? CHART_RICH_NAME_COLOR_DARK
      : CHART_RICH_NAME_COLOR_LIGHT;
    const richValueColor = this.darkMode
      ? CHART_RICH_VALUE_COLOR_DARK
      : CHART_RICH_VALUE_COLOR_LIGHT;
    return {
      color: CHART_COLORS,
      tooltip: {
        trigger: "item",
        ...(this.darkMode
          ? {
              backgroundColor: CHART_TOOLTIP_BG_DARK,
              textStyle: { color: CHART_TOOLTIP_TEXT_DARK },
              borderColor: CHART_TOOLTIP_TEXT_DARK,
            }
          : {}),
        formatter: (params: any) => {
          const value = formatAsBytes
            ? self.formatBytes(params.value)
            : params.value;
          return `${params.name}<br/>${value} (${params.percent}%)`;
        },
      },
      legend: {
        type: "scroll",
        orient: "vertical",
        right: 10,
        top: 20,
        bottom: 20,
        textStyle: {
          fontSize: 11,
          overflow: "truncate",
          width: 100,
          color: textColor,
        },
      },
      series: [
        {
          name: title,
          type: "pie",
          radius: ["50%", "75%"],
          center: ["35%", "50%"],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 4,
            borderColor: "#fff",
            borderWidth: 2,
          },
          label: {
            show: false,
            position: "center",
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: "bold",
              formatter: (params: any) => {
                const value = formatAsBytes
                  ? self.formatBytes(params.value)
                  : params.value;
                return `{name|${params.name}}\n{value|${value}}`;
              },
              rich: {
                name: {
                  fontSize: 12,
                  color: richNameColor,
                  padding: [0, 0, 4, 0],
                },
                value: {
                  fontSize: 16,
                  fontWeight: "bold",
                  color: richValueColor,
                },
              },
            },
          },
          labelLine: {
            show: false,
          },
          data,
        },
      ],
    };
  }

  sortData(sort: Sort): void {
    const data = this.workspaces.slice();
    if (!sort.active || sort.direction === "") {
      this.workspaces = data;
      return;
    }

    const direction = sort.direction === "asc" ? 1 : -1;

    const getSortable = (v: any) => {
      if (v === null || v === undefined) return "";
      if (typeof v === "number") return v;
      // Try date
      const parsed = Date.parse(String(v));
      if (!isNaN(parsed)) return parsed;
      const asNum = Number(v);
      if (!isNaN(asNum)) return asNum;
      return String(v).toLowerCase();
    };

    this.workspaces = data.sort((a, b) => {
      const aVal = getSortable((a as any)[sort.active]);
      const bVal = getSortable((b as any)[sort.active]);

      if (aVal < bVal) return -1 * direction;
      if (aVal > bVal) return 1 * direction;
      return 0;
    });
  }

  deleteWorkspace(workspace: WorkspaceDetails): void {
    const details: ConfirmationDetails = new ConfirmationDetails();
    details.title = `Are you sure you want to delete workspace, "${workspace.label}"?`;
    details.message =
      "This will permanently delete all data sources, models, and members. You cannot undo this action.";
    details.buttonLabel = "Delete";
    details.destructiveButton = true;
    details.onConfirm = async () => {
      // Convert WorkspaceDetails to Workspace for the action
      const workspaceForDelete: Workspace = {
        uuid: workspace.uuid,
        name: workspace.name,
        label: workspace.label,
        owner: workspace.owner,
      };
      // Pass navigate: false to stay on this view after deletion
      this.store.dispatch(new DeleteWorkspace(workspaceForDelete, false));
      // Refresh the list after a short delay to allow the delete to complete
      setTimeout(() => this.loadWorkspaces(), 1000);
    };
    this.confirmationService.confirm(details);
  }

  isActualOwner(workspace: WorkspaceDetails): boolean {
    return this.userID === workspace.owner;
  }

  isOwner(workspace: WorkspaceDetails): boolean {
    return this.isActualOwner(workspace) || this.isPlatformAdmin;
  }

  formatBytes(bytes: number): string {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  }
}
